<template>
  <div class="d-flex align-items-center">
    <VueMultiselect
      id="ajax"
      v-model="filter"
      :multiple="false"
      :options="items.items"
      :searchable="true"
      :close-on-select="true"
      :show-labels="false"
      label="name"
      placeholder="Search Organization"
      :options-limit="300"
      @select="updateSelectedOrg"
      @remove="handleDeselect"
      @close="handleDropdownClose"
      track-by="id"
      :loading="loading"
      class="orgs-dropdown"
      @search-change="handleOrgSearch"
      :internal-search="false"
    >
      <template #option="{ option }">
        <img
          class="rounded-circle w-40 h-40"
          v-if="option.avatar"
          :src="option.avatar"
          alt="Organization avatar"
        />
        <div class="avatar-placeholder w-40 h-40" v-else />
        <span class="option__title px-10">{{ option.name }}</span>
      </template>
    </VueMultiselect>
  </div>
</template>

<script>
import {
  defineComponent, ref, onMounted, reactive, watch,
} from 'vue';
import shared from '@/views/manage-users-and-organizations/shared';
import { useStore } from 'vuex';
import VueMultiselect from 'vue-multiselect';
import globalShared from '@/shared';

export default defineComponent({
  name: 'OrgsDropdown',
  components: { VueMultiselect },
  setup() {
    const store = useStore();
    const {
      router,
      loading,
      getOrganizationsV2,
    } = shared();

    const {
      isOrgAdmin,
    } = globalShared();

    const filter = ref('');
    const items = reactive({ items: [] });
    const allItems = reactive({ items: [] });
    const serverError = ref('');
    const sendingInvite = ref(false);
    const previousSelection = ref(null);
    const isSearching = ref(false);
    const lastSearchQuery = ref('');
    const textFilter = ref('');
    const isFiltered = ref(false);

    const baseInput = reactive({
      ids: null,
      entity: null,
      entityIds: null,
      isActive: null,
      statuses: 'All',
      query: null,
      page: 1,
      pageSize: 20,
    });

    function handleDeselect() {
      previousSelection.value = null;
      filter.value = null;
      localStorage.removeItem('selectedOrganization');
      store.dispatch('organizations/SET_SELECTED_ORGANIZATION', null);
      router.push('/manage/orgs/');
    }

    function updateSelectedOrg(itm) {
      if (itm && itm.onboarded) {
        localStorage.setItem('selectedOrganization', JSON.stringify(itm));
        store.dispatch('organizations/SET_SELECTED_ORGANIZATION', itm);
      }
    }

    function routeToOrg(itm) {
      if (itm && itm.onboarded) {
        localStorage.setItem('selectedOrganization', JSON.stringify(itm));
        store.dispatch('organizations/SET_SELECTED_ORGANIZATION', itm);

        const currentPath = router.currentRoute.value.path;

        if (currentPath.includes('/manage/orgs/') && !currentPath.endsWith('/orgs')) {
          const newPath = currentPath.replace(/\/manage\/orgs\/[^/]+/, `/manage/orgs/${itm.id}`);
          router.push(newPath);
        } else {
          router.push(`/manage/orgs/${itm.id}/edit`);
        }
      } else {
        router.push(`/manage/invites/${itm.id}/info`);
      }
    }

    function selectFirstOrganization() {
      if (items.items.length > 0 && !filter.value) {
        const firstOrg = items.items[0];
        if (firstOrg.onboarded) {
          filter.value = firstOrg;
          previousSelection.value = firstOrg;
          store.dispatch('organizations/SET_SELECTED_ORGANIZATION', firstOrg);
          localStorage.setItem('selectedOrganization', JSON.stringify(firstOrg));
        }
      }
    }

    async function findOrganizationsV2() {
      if (store.getters['users/isOrgAdmin'] || store.getters['users/isAdmin']) {
        try {
          loading.value = true;
          baseInput.pageSize = 20;
          allItems.items = await getOrganizationsV2(baseInput);
          items.items = allItems.items;
          items.items.sort((a, b) => (a.name > b.name ? 1 : -1));

          const selectedOrg = store.getters['organizations/GET_SELECTED_ORGANIZATION'];
          if (!selectedOrg) {
            selectFirstOrganization();
          }
        } catch (err) {
          console.error('Error loading organizations:', err);
        } finally {
          loading.value = false;
        }
      }
    }

    async function searchFilter() {
      baseInput.ids = null;
      baseInput.entity = null;
      baseInput.entityIds = null;
      baseInput.isActive = null;
      baseInput.statuses = 'All';
      baseInput.query = textFilter.value || null;
      baseInput.page = 1;
      baseInput.pageSize = textFilter.value ? -1 : 20;

      if (textFilter.value === '') {
        isFiltered.value = false;
      } else {
        isFiltered.value = true;
      }

      await findOrganizationsV2();
    }

    async function clearTextFilter() {
      textFilter.value = '';
      isFiltered.value = false;
      baseInput.query = null;
      baseInput.pageSize = 20;
      await findOrganizationsV2();
    }

    async function handleOrgSearch(query) {
      if (isSearching.value) {
        return;
      }

      lastSearchQuery.value = query || '';
      textFilter.value = query || '';

      if (query && query.length > 2) {
        isSearching.value = true;
        await searchFilter();
        isSearching.value = false;
      } else if (query === '' || query.length === 0) {
        lastSearchQuery.value = '';
        await clearTextFilter();
      }
    }

    function handleDropdownClose() {
      if (lastSearchQuery.value && !filter.value) {
        setTimeout(async () => {
          await clearTextFilter();
        }, 100);
      }
    }

    function showOnboardedStatus(item) {
      return item.onboarded ? 'Onboarded' : '--';
    }

    function showActiveStatus(item) {
      return item.active ? 'Yes' : 'No';
    }

    onMounted(async () => {
      await findOrganizationsV2();

      const storeSelectedOrg = store.getters['organizations/GET_SELECTED_ORGANIZATION'];

      const savedOrganization = localStorage.getItem('selectedOrganization');

      if (storeSelectedOrg) {
        const isValidOrganization = items.items.find((org) => org.id === storeSelectedOrg.id);
        if (isValidOrganization) {
          filter.value = storeSelectedOrg;
          previousSelection.value = storeSelectedOrg;
        }
      } else if (savedOrganization) {
        try {
          const parsedOrganization = JSON.parse(savedOrganization);

          const isValidOrganization = items.items.find((org) => org.id === parsedOrganization.id);
          if (isValidOrganization) {
            filter.value = parsedOrganization;
            previousSelection.value = parsedOrganization;
            await store.dispatch('organizations/SET_SELECTED_ORGANIZATION', parsedOrganization);
          } else {
            localStorage.removeItem('selectedOrganization');
            selectFirstOrganization();
          }
        } catch (err) {
          console.error('Error parsing saved organization:', err);
          localStorage.removeItem('selectedOrganization');
          selectFirstOrganization();
        }
      } else {
        selectFirstOrganization();
      }
    });

    watch(() => store.getters['organizations/GET_SELECTED_ORGANIZATION'], async (newSelectedOrg, oldSelectedOrg) => {
      if (newSelectedOrg?.id === oldSelectedOrg?.id) {
        return;
      }

      if (newSelectedOrg) {
        // Find the organization in our current items
        const matchingOrg = items.items.find((org) => org.id === newSelectedOrg.id);

        if (matchingOrg) {
          // Update dropdown selection only if it's different
          if (!filter.value || filter.value.id !== matchingOrg.id) {
            filter.value = matchingOrg;
            previousSelection.value = matchingOrg;
            localStorage.setItem('selectedOrganization', JSON.stringify(matchingOrg));
          }
        } else {
          // Organization not in current list, set it directly from store
          filter.value = newSelectedOrg;
          previousSelection.value = newSelectedOrg;
          localStorage.setItem('selectedOrganization', JSON.stringify(newSelectedOrg));
        }
      } else {
        // Organization was cleared/deselected
        filter.value = null;
        previousSelection.value = null;
        localStorage.removeItem('selectedOrganization');
      }
    }, { immediate: true });

    // Watch for dropdown selection changes (user selection)
    watch(filter, (newVal, oldVal) => {
      // Skip if the value hasn't actually changed
      if (newVal?.id === oldVal?.id) {
        return;
      }

      const hasValidNewVal = newVal && newVal.id;
      const isDifferentFromPrevious = !previousSelection.value
        || previousSelection.value.id !== newVal.id;

      if (hasValidNewVal && isDifferentFromPrevious) {
        store.dispatch('organizations/SET_SELECTED_ORGANIZATION', newVal);
        previousSelection.value = newVal;
        localStorage.setItem('selectedOrganization', JSON.stringify(newVal));
      }
    });

    // Watch for route parameter changes
    watch(() => router.currentRoute.value.params.id, async (newId, oldId) => {
      if (newId && newId !== oldId && router.currentRoute.value.path.includes('/manage/orgs/')) {
        const selectedOrg = store.getters['organizations/GET_SELECTED_ORGANIZATION'];

        if (!selectedOrg || selectedOrg.id !== newId) {
          const orgFromList = items.items.find((org) => org.id === newId);

          if (orgFromList) {
            filter.value = orgFromList;
            previousSelection.value = orgFromList;
            await store.dispatch('organizations/SET_SELECTED_ORGANIZATION', orgFromList);
            localStorage.setItem('selectedOrganization', JSON.stringify(orgFromList));
          } else {
            console.warn('Could not find organization for route ID:', newId);
          }
        }
      }
    }, { immediate: true });

    return {
      loading,
      filter,
      items,
      serverError,
      sendingInvite,
      isOrgAdmin,
      showOnboardedStatus,
      showActiveStatus,
      routeToOrg,
      updateSelectedOrg,
      handleDeselect,
      handleOrgSearch,
      handleDropdownClose,
      textFilter,
      isFiltered,
      baseInput,
      searchFilter,
      clearTextFilter,
      findOrganizationsV2,
    };
  },
});
</script>

<style lang="scss" scoped>
.orgs-dropdown {
  width: 280px;

  :deep(.multiselect) {
    min-height: 44px;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    transition: all 0.2s ease;
    background: #ffffff;

    &:hover {
      border-color: #94a3b8;
    }

    &.multiselect--active {
      border-color: #2563eb;
      box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
    }

    .multiselect__tags {
      min-height: 44px;
      padding: 6px 40px 6px 12px;
      border: none;
      background: transparent;
      border-radius: 10px;

      .multiselect__single {
        background: transparent;
        margin: 0;
        padding: 4px 0;
        font-size: 15px;
        color: #0f172a;
        font-weight: 500;
      }

      .multiselect__placeholder {
        color: #64748b;
        padding: 4px 0;
        margin: 0;
        font-size: 15px;
      }

      .multiselect__input {
        background: transparent;
        padding: 4px 0;
        margin: 0;
        font-size: 15px;
        color: #0f172a;

        &::placeholder {
          color: #64748b;
        }
      }
    }

    .multiselect__content-wrapper {
      margin-top: 5px;
      border: none;
      border-radius: 12px;
      box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
      background: #ffffff;
      max-height: 300px;
      overflow-y: auto;
      padding: 6px;

      &::-webkit-scrollbar {
        width: 6px;
      }

      &::-webkit-scrollbar-track {
        background: #f8fafc;
        border-radius: 3px;
      }

      &::-webkit-scrollbar-thumb {
        background: #cbd5e1;
        border-radius: 3px;

        &:hover {
          background: #94a3b8;
        }
      }

      .multiselect__option {
        padding: 10px 12px;
        min-height: 44px;
        display: flex !important;
        align-items: center;
        gap: 12px;
        font-size: 14px;
        color: #0f172a;
        transition: all 0.2s ease;
        border-radius: 8px;
        margin: 2px 0;

        &:hover {
          background: #f8fafc;
        }

        &.multiselect__option--highlight {
          background: #eff6ff;
          color: #2563eb;
        }

        &.multiselect__option--selected {
          background: #dbeafe;
          color: #1e40af;
          font-weight: 500;
        }
      }
    }

    .multiselect__spinner {
      background: #ffffff;
      border-radius: 8px;
      width: 40px;
      height: 40px;
      right: 2px;

      &:before, &:after {
        border-color: #2563eb transparent transparent;
      }
    }

    .multiselect__select {
      height: 44px;
      width: 40px;
      padding: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s ease;

      &:before {
        border-color: #64748b transparent transparent;
        border-width: 5px 5px 0 5px;
        margin-top: 0;
        top: auto;
      }
    }
  }
}

.rounded-circle {
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #e2e8f0;
}

.w-40 {
  width: 36px;
  height: 36px;
  min-width: 36px;
}

.h-40 {
  height: 36px;
}

.avatar-placeholder {
  background: #f1f5f9;
  border-radius: 50%;
  border: 2px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  font-size: 16px;

  &:after {
    content: '👥';
  }
}

.px-10 {
  padding-left: 12px;
  padding-right: 12px;
}

.option__title {
  font-size: 14px;
  font-weight: 500;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  line-height: 1.4;
}
</style>
